import { useStaticQuery, graphql } from "gatsby";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";
import { find } from "lodash";

import Button from "~/components/buttons/Button";
import TextButton from "~/components/buttons/TextButton";
import GatedDocumentButton from "~/components/buttons/GatedDocumentButton";

import FaIcon from "~/components/FaIcon";
import CADIcon from "~/components/CADIcon";

import Form from "~/queryBlocks/Form";

import useStringTranslation from "~/hooks/translations/useStringTranslation";

import transition from "~/utilities/transition";

const Wrapper = styled.div`
    text-align: ${({ align }) => align ?? "center"};
`;

const StyledTextButton = styled(TextButton)`
    ${transition({}, "color")};
    font-size: 1.6rem !important;
    color: ${({ theme }) => theme.colors.typography.links};

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.colors.typography.linksHover};
    }
`;

const StyledIcon = styled(FaIcon)`
    margin-right: ${({ buttonText }) => (buttonText ? "10px" : "")};
`;

const StyledGatedButton = styled(GatedDocumentButton)`
    ${transition({}, "color")};
    color: ${({ theme, inline }) =>
        inline ? theme.colors.typography.links : theme.palette.white};

    &:hover,
    &:focus {
        color: ${({ theme, inline }) =>
            inline ? theme.colors.typography.linksHover : theme.palette.white};
    }
`;

const DataSheetTableButton = ({
    primaryCategory,
    productCategories,
    sku,
    slug,
    dataSheet = null,
    requestDownload = false,
    inline = true,
    icon = "download",
    color = "purple",
    buttonText = "",
    align = "center",
}) => {
    const {
        wp: { formsOptionsPage },
    } = useStaticQuery(graphql`
        query StaticDataSheetTableButtonQuery {
            wp {
                formsOptionsPage {
                    requestDataSheetForms {
                        requestDataSheetForms {
                            categorySlug
                            formId
                        }
                    }
                }
            }
        }
    `);

    const { requestNowText, requestDataSheetText } = useStringTranslation([
        { text: "Request Now", handle: "requestNowText" },
        { text: "Request Data Sheet", handle: "requestDataSheetText" },
    ]);

    const requestDataSheetForms =
        formsOptionsPage?.requestDataSheetForms?.requestDataSheetForms;

    const form = find(
        requestDataSheetForms,
        ({ categorySlug }) => primaryCategory.slug === categorySlug,
    );

    const isCad = icon === "cad";

    return (
        <Wrapper {...{ align }}>
            {requestDownload && form && !isCad ? (
                <Form
                    modalForm
                    formId={form.formId}
                    formPresetValues={{
                        sku,
                        productUrl: `${slug}`,
                        productCategory: primaryCategory.slug,
                        subcategory: productCategories?.nodes[0]?.name,
                    }}
                    alignButton="center"
                    altButton={
                        inline ? (
                            <StyledTextButton aria-label={requestNowText}>
                                {requestNowText}
                            </StyledTextButton>
                        ) : (
                            <Button type="button" {...{ color }}>
                                {isCad ? (
                                    <CADIcon />
                                ) : (
                                    <StyledIcon
                                        name={icon}
                                        {...{ inline, buttonText }}
                                    />
                                )}
                                {requestDataSheetText}
                            </Button>
                        )
                    }
                />
            ) : (
                <StyledGatedButton
                    doc={dataSheet}
                    {...{ isCad, sku, inline, color, buttonText }}
                >
                    {isCad ? (
                        <CADIcon />
                    ) : (
                        <StyledIcon name={icon} {...{ inline, buttonText }} />
                    )}
                    {buttonText ?? ""}
                </StyledGatedButton>
            )}
        </Wrapper>
    );
};

DataSheetTableButton.propTypes = {
    dataSheet: PropTypes.shape({}),
    requestDownload: PropTypes.bool,
    inline: PropTypes.bool,
    icon: PropTypes.string,
    color: PropTypes.string,
    buttonText: PropTypes.string,
    align: PropTypes.string,
};

export default DataSheetTableButton;
